import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IAM_API, iam_api_requests, public_iam_api_requests } from '..';
import type { ServerSideDataGridResponse } from '../../../hooks';

const PATH = '/help-center';

/** Prefix shared by every inbox query. A status change invalidates it, so the grid and the details refresh. */
const INBOX_QUERY_KEY = [IAM_API, 'help-center-inbox'];

export type HelpRequestIssueType = 'FEEDBACK' | 'COMPLAINT' | 'OTHER';
export type HelpRequestSource = 'WEB' | 'MOBILE';
export type HelpRequestStatus = 'OPEN' | 'IN_PROGRESS' | 'CLOSED';

export interface HelpCenterSubmitRequest {
	issueType: HelpRequestIssueType;
	description: string;
	phone: string;
	source: HelpRequestSource;
	appVersion?: string;
}

export interface HelpCenterSubmitResponse {
	id: number;
}

/** One inbox row. createTimestamp is UTC epoch seconds; userName is null when the user no longer exists. */
export interface HelpRequestSummary {
	id: number;
	createTimestamp: number;
	issueType: HelpRequestIssueType;
	phone: string;
	/** Null when the request was sent by a guest (not signed in). */
	userId: number | null;
	userName: string | null;
	descriptionPreview: string;
	source: HelpRequestSource;
	appVersion: string | null;
	status: HelpRequestStatus;
}

export interface HelpRequestUserRef {
	id: number;
	name: string | null;
}

/** Full request for the details dialog. The statusChanged* fields and statusNote are null until the first change. */
export interface HelpRequestDetails {
	id: number;
	createTimestamp: number;
	updateTimestamp: number | null;
	issueType: HelpRequestIssueType;
	description: string;
	phone: string;
	/** Null when the request was sent by a guest (not signed in). */
	userId: number | null;
	userName: string | null;
	source: HelpRequestSource;
	appVersion: string | null;
	status: HelpRequestStatus;
	statusChangedBy: HelpRequestUserRef | null;
	statusChangedAt: number | null;
	statusNote: string | null;
}

export interface HelpRequestStatusUpdate {
	status: HelpRequestStatus;
	note?: string;
}

/** Allowed status changes, same table as HelpRequestStatus.canChangeTo in the API. */
export const ALLOWED_STATUS_CHANGES: Record<HelpRequestStatus, HelpRequestStatus[]> = {
	OPEN: ['IN_PROGRESS', 'CLOSED'],
	IN_PROGRESS: ['CLOSED'],
	CLOSED: ['OPEN'],
};

/**
 * Send a Help Center request (POST /requests, 201 {id}). Signed in: sent with the session, so the API links the user.
 * Guest: sent without auth headers, because iam_api_requests cancels requests that have no sessionId (getAuthHeader).
 */
const useSubmitHelpRequest = () => {
	return useMutation({
		mutationFn: async (
			request: HelpCenterSubmitRequest
		): Promise<HelpCenterSubmitResponse> =>
			(localStorage.getItem('sessionId') ? iam_api_requests : public_iam_api_requests).post(
				`${PATH}/requests`,
				request
			),
	});
};

/** One inbox page for useServerSideDataGrid. Filters are left out when not set (the API then returns all). */
const searchHelpRequests = async ({
	status,
	issueType,
	page,
	size,
	sort,
}: {
	status?: HelpRequestStatus;
	issueType?: HelpRequestIssueType;
	page: number;
	size: number;
	sort: string;
}): Promise<ServerSideDataGridResponse<HelpRequestSummary>> =>
	iam_api_requests.get(`${PATH}/inbox/requests`, {
		params: { status, issueType, page, size, sort: sort || undefined },
	});

const useGetHelpRequest = (id: number | null) => {
	return useQuery({
		queryKey: [...INBOX_QUERY_KEY, 'details', id],
		queryFn: async (): Promise<HelpRequestDetails> =>
			iam_api_requests.get(`${PATH}/inbox/requests/${id}`),
		enabled: id != null,
		staleTime: 0,
		refetchOnWindowFocus: false,
	});
};

const useChangeHelpRequestStatus = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			id,
			request,
		}: {
			id: number;
			request: HelpRequestStatusUpdate;
		}): Promise<HelpRequestDetails> =>
			iam_api_requests.put(`${PATH}/inbox/requests/${id}/status`, request),

		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: INBOX_QUERY_KEY });
		},
	});
};

export {
	INBOX_QUERY_KEY as HELP_CENTER_INBOX_QUERY_KEY,
	useSubmitHelpRequest,
	searchHelpRequests,
	useGetHelpRequest,
	useChangeHelpRequestStatus,
};
